import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import styled from "@emotion/styled";
import { ApiService } from "src/common";
import { errorA11yProps, errorIdFor } from "src/common/style/a11y.style";
import { colors } from "src/theme";
import { InputLabel } from "../typography";

const Box = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
})

const Input = styled.input((props: { hasError: boolean }) => ({
  boxSizing: 'border-box' as const,
  outline: 'none',
  display: 'block',
  width: '100%',
  height: '44px',
  padding: '9px 12px',
  fontSize: '18px',
  fontWeight: 400,
  fontFamily: "Red Hat Display",
  fontStyle: 'normal',
  lineHeight: '24px',
  color: colors.text1,
  backgroundColor: 'transparent',
  borderRadius: '8px',
  border: `1px solid ${props.hasError ? colors.error1 : colors.border5}`,
  transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',

  '&[disabled]': {
    border: `1px solid ${colors.border6}`,
    color: colors.text4,
    pointerEvents: 'none' as const,
  },

  '&::-moz-placeholder': {
    color: colors.text4,
    opacity: 1,
  },

  '&:-ms-input-placeholder': {
    color: colors.text4,
  },

  '&::-webkit-input-placeholder': {
    color: colors.text4,
  },

  '&:focus::-moz-placeholder': {
    color: 'transparent',
    opacity: 1,
  },

  '&:focus:-ms-input-placeholder': {
    color: 'transparent',
  },

  '&:focus::-webkit-input-placeholder': {
    color: 'transparent',
  },

  '&:focus': {
    borderColor: colors.border4,
  },

  '&[type=number]::-webkit-inner-spin-button, &[type=number]::-webkit-outer-spin-button': {
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
    appearance: 'none' as const,
  },
}));

const ValidationError = styled.div({
  position: 'absolute',
  color: colors.error1,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '1.14',
  marginBottom: 0,
  paddingTop: '4px',
  transition: 'all 0.4s ease',
});

const StreetList = styled.ul({
  margin: 0,
  listStyle: 'none',
  width: '100%',
  boxSizing: 'border-box' as const,
  outline: 'none',
  display: 'block',
  padding: '9px 12px',
  fontSize: '18px',
  fontWeight: 400,
  fontStyle: 'normal',
  lineHeight: '24px',
  color: colors.text1,
  borderRadius: '8px',
  border: `1px solid ${colors.border5}`,
  transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',
  backgroundColor: '#fff',
  maxHeight: '150px',
  overflowY: 'auto' as const,
  zIndex: 99,
});

const StreetItem = styled.li((props: { isActive?: boolean }) => ({
  cursor: 'pointer',
  padding: '2px 4px',
  borderRadius: '4px',
  backgroundColor: props.isActive ? colors.background6 : 'transparent',
  outline: props.isActive ? `2px solid ${colors.primary}` : 'none',
  outlineOffset: '-2px',

  '&:hover': {
    backgroundColor: colors.background6,
  }
}))

export type InputFieldPropsStreet = {
  id?: string;
  label?: string;
  value?: string | number;
  error?: string;
  disabled?: boolean;
  onChange?: (value: { street: string, streetKompareId: string, city: string, cityKompareId: string, zipCode: number }, isValid: boolean) => void;
  cityKompareId?: string;
};

interface StreetData {
  readonly streetName: string
  readonly streetKompareId: string
  readonly cityKompareId: string
  readonly cityName: string
  readonly postalCode: number
}

export const InputStreet: React.FC<InputFieldPropsStreet> = ({
  label,
  value,
  error,
  disabled,
  onChange,
  cityKompareId
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [showStreetList, setShowStreetList] = useState(false);
  const [streetSelected, setStreetSelected] = useState(false);
  const [inputTimeout, setInputTimeout] = useState<NodeJS.Timeout | null>(null);
  const [streetData, setStreetData] = useState<StreetData[]>([]);
  // Index in streetData highlighted by the keyboard, -1 when none
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef<HTMLUListElement>(null);
  // Chrome ignores autocomplete="off" on fields it classifies as address fields (from the id, name or
  // label text, e.g. "Ulica"), so the input gets a neutral generated id and an unrecognized
  // autocomplete token, which Chrome does not autofill
  const inputId = `combobox${useId().replace(/:/g, '')}`;
  const listboxId = `${inputId}-listbox`;
  const optionId = (index: number) => `${inputId}-option-${index}`;

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const fetchStreets = (query: string) => {
    ApiService.getStreets(query, cityKompareId).subscribe({
      next: (streets: StreetData[]) => {
        setStreetData(streets);
        setActiveIndex(-1);
      }
    });
  };

  const fetchStreetsDebounced = useCallback((query: string) => {
    if (inputTimeout) {
      clearTimeout(inputTimeout);
    }

    const timeout = setTimeout(() => {
      fetchStreets(query);
    }, 500);

    setInputTimeout(timeout);
  }, [inputTimeout]);

  const resetStreetSelected = () => {
    setStreetSelected(false);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const newValue = e.target.value;

    setInputValue(newValue);

    fetchStreetsDebounced(newValue);

    setShowStreetList(true);
    resetStreetSelected();
  };

  const handleStreetItemClick = (street: StreetData) => {
    setInputValue(street.streetName);
    setStreetSelected(true);
    setShowStreetList(false);
    setActiveIndex(-1);
    if (onChange) {
      onChange({ streetKompareId: street.streetKompareId, street: street.streetName, city: street.cityName, cityKompareId: street.cityKompareId, zipCode: street.postalCode }, !!street.streetKompareId);
    }
  };

  const selectableStreets = streetData.filter(street => street.streetName);
  const isExpanded = showStreetList && selectableStreets.length > 0 && !streetSelected;

  // Keep the highlighted option scrolled into view
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    listRef.current.querySelector<HTMLElement>(`#${CSS.escape(optionId(activeIndex))}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const count = selectableStreets.length;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (count === 0) return;
        if (!isExpanded) { setShowStreetList(true); setStreetSelected(false); }
        setActiveIndex(prev => (prev + 1) % count);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (count === 0) return;
        if (!isExpanded) { setShowStreetList(true); setStreetSelected(false); }
        setActiveIndex(prev => (prev <= 0 ? count - 1 : prev - 1));
        break;
      case 'Enter':
        if (isExpanded && activeIndex >= 0 && activeIndex < count) {
          e.preventDefault();
          handleStreetItemClick(selectableStreets[activeIndex]);
        }
        break;
      case 'Escape':
        if (isExpanded) {
          e.preventDefault();
          setShowStreetList(false);
          setActiveIndex(-1);
        }
        break;
      case 'Tab':
        setShowStreetList(false);
        setActiveIndex(-1);
        break;
    }
  };

  return (
    <Box>
      <div>
        {label ? <InputLabel htmlFor={inputId} disabled={disabled}>{label}</InputLabel> : null}
        <Input
          autoComplete="kompare-off"
          id={inputId}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isExpanded}
          aria-controls={listboxId}
          aria-activedescendant={isExpanded && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          {...errorA11yProps(inputId, error)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            setShowStreetList(false);
            setActiveIndex(-1);
          }}
          type='text'
          value={inputValue}
          disabled={disabled}
          onChange={handleChange}
          hasError={!!error}
        />
        {error ? <ValidationError id={errorIdFor(inputId)}>{error}</ValidationError> : null}
      </div>
      {showStreetList && streetData && streetData.length > 0 && !streetSelected && (
        <StreetList id={listboxId} role="listbox" ref={listRef} aria-label={label}>
          {selectableStreets.length === 0 ? (
            <StreetItem role="option" aria-disabled="true">Ne postoji ulica</StreetItem>
          ) : (
            selectableStreets.map((street, index) => (
              <StreetItem
                key={street.streetKompareId}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                isActive={index === activeIndex}
                // Keep focus in the input so blur does not close the list before the click lands
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleStreetItemClick(street)}
              >
                {cityKompareId ? street.streetName : `${street.streetName}, ${street.cityName} ${street.postalCode}`}
              </StreetItem>
            ))
          )}
        </StreetList>
      )}
    </Box>
  );
};
